import Decimal from 'decimal.js';
import { DeepPartial, Raw } from '@/modules/common/helpers/api';
import { Data, PbInstrumentInfo } from '@/connect';
import { SecurityCategory, SecurityType } from '@/connect/gen/consts/instrumentconsts_pb';

export type RawSecurity = Omit<Raw<Security>, 'hasCorporateActions'>;

export type CorporateActions = Record<string, true>;

export class Security {
  public id: string;
  public cusip: string;
  public ticker: string;
  public securityType: SecurityType;
  public securityCategory: SecurityCategory;
  public name: string;
  public lastClosePrice: Decimal;
  public cannotTradeMessage: string | null;
  public hasCorporateActions?: boolean;

  protected constructor(data: RawSecurity, corporateActions: CorporateActions) {
    this.id = data.id;
    this.cusip = data.cusip;
    this.ticker = data.ticker;
    this.securityType = data.securityType;
    this.securityCategory = data.securityCategory;
    this.name = data.name;
    this.lastClosePrice = new Decimal(data.lastClosePrice);
    this.cannotTradeMessage = data.cannotTradeMessage;
    this.hasCorporateActions = Object.keys(corporateActions).includes(this.cusip);
  }

  // fromPbInstrumentInfo creates a Security from a PbInstrumentInfo
  // unfortunately this isn't perfect because PbInstrumentInfo contains slightly less info
  // but will do the trick in most cases
  public static fromPbInstrumentInfo(data: Data<PbInstrumentInfo>): Security {
    return new Security(
      {
        id: '',
        cusip: data.cusip,
        ticker: data.ticker,
        securityType: data.securityType,
        securityCategory: data.securityCategory,
        name: data.name,
        lastClosePrice: data.lastClosePrice || '0',
        cannotTradeMessage: data.cannotTradeMessage || null,
      },
      {}
    );
  }

  public static fromData(data: RawSecurity): Security;
  public static fromData(data: RawSecurity | null): Security | null;
  public static fromData(data: RawSecurity | undefined): Security | undefined;
  public static fromData(data: RawSecurity): null | undefined | Security {
    if (data === null) return null;
    if (data === undefined) return undefined;

    return Security.fromDataAndCorporateActions(data, undefined);
  }

  public static fromDataAndCorporateActions(
    data: RawSecurity,
    corporateActions?: CorporateActions
  ): Security;
  public static fromDataAndCorporateActions(
    data: RawSecurity | null,
    corporateActions?: CorporateActions
  ): Security | null;
  public static fromDataAndCorporateActions(
    data: RawSecurity | undefined,
    corporateActions?: CorporateActions
  ): Security | undefined;
  public static fromDataAndCorporateActions(
    data: RawSecurity,
    corporateActions: CorporateActions = {}
  ): null | undefined | Security {
    if (data === null) return null;
    if (data === undefined) return undefined;

    return new Security(data, corporateActions);
  }

  public static toData(model: Security): RawSecurity {
    return {
      id: model.id,
      cusip: model.cusip,
      ticker: model.ticker,
      securityType: model.securityType,
      securityCategory: model.securityCategory,
      name: model.name,
      lastClosePrice: model.lastClosePrice.toString(),
      cannotTradeMessage: model.cannotTradeMessage,
    };
  }

  public static mock(data?: null | DeepPartial<RawSecurity>): Security {
    return Security.fromData(Security.mockData(data));
  }

  public static mockData(data?: null | DeepPartial<RawSecurity>): RawSecurity {
    return {
      id: '1',
      cusip: '123456789',
      ticker: 'IBM',
      securityType: SecurityType.CS,
      securityCategory: SecurityCategory.EQUITY,
      name: 'IBM',
      lastClosePrice: '120.0',
      cannotTradeMessage: null,
      ...data,
    };
  }
}

export type RawAuctionSecurity = Raw<
  AuctionSecurity,
  { securityType: string; securityCategory: string }
>;

// TODO: move somewhere else
export class AuctionSecurity {
  public id: string;
  public cusip: string;
  public ticker: string;
  public securityType: SecurityType;
  public securityCategory: SecurityCategory;
  public isAuroraActive: boolean;
  public isAuroraRestricted: boolean;
  public hasActivePriceDataSource: boolean;
  public isCusipActive: boolean;
  public nsccNewEligible: boolean;
  public nsccRollEligible: boolean;
  public marketplaceEligible: boolean;
  public tradeManagementEligible: boolean;
  public occEligible: boolean;
  public name: string;
  public type: string;
  public currencyCode: string;
  public isinCode: string | null;
  public cik: string | null;
  public compositeFigi: string | null;
  public closePrice: Decimal;
  public yield: Decimal | null;
  public securitySource: string | null;

  protected constructor(data: RawAuctionSecurity) {
    this.id = data.id;
    this.cusip = data.cusip;
    this.ticker = data.ticker;
    // API returns these as strings (e.g., "CS"), convert to enum values
    this.securityType = SecurityType[data.securityType as keyof typeof SecurityType];
    this.securityCategory =
      SecurityCategory[data.securityCategory as keyof typeof SecurityCategory];
    this.isAuroraActive = data.isAuroraActive;
    this.isAuroraRestricted = data.isAuroraRestricted;
    this.hasActivePriceDataSource = data.hasActivePriceDataSource;
    this.isCusipActive = data.isCusipActive;
    this.nsccNewEligible = data.nsccNewEligible;
    this.nsccRollEligible = data.nsccRollEligible;
    this.name = data.name;
    this.type = data.type;
    this.currencyCode = data.currencyCode;
    this.isinCode = data.isinCode;
    this.cik = data.cik;
    this.compositeFigi = data.compositeFigi;
    this.closePrice = new Decimal(data.closePrice);
    this.marketplaceEligible = data.marketplaceEligible;
    this.occEligible = data.occEligible;
    this.tradeManagementEligible = data.tradeManagementEligible;
    this.yield = data.yield != null ? new Decimal(data.yield) : null;
    this.securitySource = data.securitySource;
  }

  public static fromData(data: RawAuctionSecurity): AuctionSecurity;
  public static fromData(data: RawAuctionSecurity | null): AuctionSecurity | null;
  public static fromData(data: RawAuctionSecurity | undefined): AuctionSecurity | undefined;
  public static fromData(data: RawAuctionSecurity): null | undefined | AuctionSecurity {
    if (data === null) return null;
    if (data === undefined) return undefined;

    return new AuctionSecurity(data);
  }

  public static toData(model: AuctionSecurity): RawAuctionSecurity {
    return {
      ...model,
      closePrice: model.closePrice.toString(),
      securityType: SecurityType[model.securityType] as string,
      securityCategory: SecurityCategory[model.securityCategory] as string,
      yield: null,
    };
  }

  public static mock(data?: DeepPartial<RawAuctionSecurity>): AuctionSecurity {
    return AuctionSecurity.fromData(AuctionSecurity.mockData(data));
  }

  public static mockData(data?: DeepPartial<RawAuctionSecurity>): RawAuctionSecurity {
    return {
      id: '',
      cusip: '123456789',
      ticker: 'IBM',
      securityType: 'CS',
      securityCategory: 'EQUITY',
      isAuroraActive: true,
      isAuroraRestricted: true,
      hasActivePriceDataSource: true,
      isCusipActive: true,
      nsccNewEligible: true,
      nsccRollEligible: true,
      occEligible: true,
      marketplaceEligible: true,
      tradeManagementEligible: true,
      name: 'IBM',
      type: '',
      currencyCode: '',
      isinCode: null,
      cik: null,
      compositeFigi: null,
      closePrice: '100',
      yield: null,
      securitySource: null,
      ...data,
    };
  }
}
